import { Id } from 'src/utils/types';
import { request } from '.';
import { Service, normalizeServiceStatus } from './services';
import { supabaseHttp } from "src/utils/supabaseClient";

export const documents = {
    CNPJ: "CNPJ",
    CPF: "CPF",
    OUTRO: "OUTRO",
}

export const subscriptions = {
    PLANO: "PLANO",
    OUTRO: "OUTRO",
}

export type DocumentTypes = keyof typeof documents;

export type SubscriptionTypes = keyof typeof subscriptions;

export type CreateCompanyPayload = {
    trade_name: string;
    social_reason: string;
    subscription: SubscriptionTypes;
    responsible: string;
    document: string;
    document_type: DocumentTypes;
    service_value: number;
    monthly_value: number;
    paid_services?: number;
    coparticipations?: number;
    coparticipation_value?: number;
}

export type Company = {
    id: number;
    trade_name: string;
    social_reason: string;
    subscription: SubscriptionTypes;
    responsible: string;
    document: string;
    document_type: DocumentTypes;
    deleted_at: string|null,
    fk_deleted_by_user_id: number|null,
    collaborator_quantity: number,
    valor: number|null;
    mensalidade: number|null;
    consultas_pagas: number|null;
    coparticipativas: number|null;
    valor_cop: number|null;
    oculto?: boolean;
    mensalidade_quitada?: boolean;
}

export const isCompanyVisible = (company: Company): boolean => !Boolean(company?.oculto);

export const filterVisibleCompanies = (companies: Company[] = []): Company[] => {
    return companies.filter(isCompanyVisible);
}

export type CollaboratorProntuario = {
    medicamentos_em_uso?: string | null;
    contatos_emergencia?: Array<{
        nome: string;
        parentesco: string;
        telefone: string;
    }> | null;
};

export type Collaborator = {
    id_colaborador: number,
    id_empresa: number,
    nome: string,
    setor: string,
    cargo: string,
    unidade: string,
    observacoes: string,
    email?: string | null,
    bloqueio?: boolean,
    bloqueio_empresa?: boolean,
    last_service?: {
        annotations: string,
        complaint: string,
    },
    prontuario?: CollaboratorProntuario | null,
}

export type CreateCollaboratorPayload = {
    nome: string;
    setor: string;
    cargo: string;
    observacoes: string;
    unidade: string;
}

export type CollaboratorServicesSummary = {
    qtde_atendimentos: number;
    nome_colaborador: string;
    primeiro_atendimento: string;
    ultimo_atendimento: string;
    psicologos: string[];
}

export async function GetCompanies() {
    return await request.get<Company[]>("/empresas");
}

export const getCompanyById = async (id: number|string): Promise<Company> => {
    const res = await request.get<Company>(`/empresas/${id}`);

    return res.data;
}

export const createCompany = async (payload: CreateCompanyPayload): Promise<void> => {
    await request.post<void>("/empresas", payload);
}

export const updateCompany = async (id: number|string|undefined, payload: CreateCompanyPayload): Promise<void> => {
    await request.put<void>(`/empresas/${id}`, payload);
}

export async function updateMensalidadeQuitada(
    companyId: number | string,
    quitada: boolean
): Promise<void> {
    await request.patch(`/empresas/${companyId}/mensalidade-quitada`, {
        mensalidade_quitada: quitada,
    });
}

export async function getCollaboratorByName(name: string, companyID: number): Promise<Collaborator[]> {
    if (!name) {
        return [];
    }

    const response = await request.get<Collaborator[]>(`/empresas/${companyID}/colaboradores`, {
        params: {
            collaborator_name: name,
        }
    });

    if (!response) {
        return [];
    }

    return response.data;
}

export async function searchCollaboratorsByCompany(
  companyID: Id,
  search = "",
  activeOnly = false
): Promise<Collaborator[]> {
  if (!companyID) {
    return [];
  }

  const response = await request.get<Collaborator[]>(
    `/empresas/${companyID}/colaboradores`,
    {
      params: {
        search,
        collaborator_name: search,
        active_only: activeOnly ? "true" : undefined,
      },
    }
  );

  if (!response) {
    return [];
  }

  return response.data ?? [];
}

export async function getCollaboratorsByCompany(companyID: Id): Promise<Collaborator[]> {
  const response = await request.get<Collaborator[]>(`/empresas/${companyID}/colaboradores`);
  if (!response || !Array.isArray(response.data)) return [];

  // Ordena por ID crescente (robusto para diferentes nomes de campo)
  const sorted = response.data.slice().sort((a: any, b: any) => {
    const ida = Number(a.id_colaborador ?? a.id ?? a.idColaborador);
    const idb = Number(b.id_colaborador ?? b.id ?? b.idColaborador);

    if (Number.isFinite(ida) && Number.isFinite(idb)) return ida - idb;

    // fallback: ordena por nome se algum ID vier inválido
    const na = String(a.nome ?? a.name ?? "");
    const nb = String(b.nome ?? b.name ?? "");
    return na.localeCompare(nb, "pt-BR", { sensitivity: "base" });
  });

  return sorted;
}

// ADICIONANDO VERIFICAÇÃO DE ID DA EMPRESA ANTES DE FAZER A REQUISIÇÃO!
// ----------------------------------------------------------------

export async function getCollaboratorsByCompanyAndId(companyID: Id, collaboratorId: Id): Promise<Collaborator> {
    console.log(`[getCollaboratorsByCompanyAndId] Iniciando busca: companyID=${companyID}, collaboratorId=${collaboratorId}`);

    if (!companyID || !collaboratorId) {
        console.error("[getCollaboratorsByCompanyAndId] ERRO: Company ID e Collaborator ID são obrigatórios.");
        throw new Error("Company ID e Collaborator ID são obrigatórios.");
    }

    try {
        console.log(`[getCollaboratorsByCompanyAndId] Fazendo requisição para /empresas/${companyID}/colaboradores/${collaboratorId}`);
        const response = await request.get<Collaborator>(`/empresas/${companyID}/colaboradores/${collaboratorId}`);

        console.log("[getCollaboratorsByCompanyAndId] Resposta recebida com sucesso:", response.data);
        return response.data;
    } catch (error: any) {
        console.error("[getCollaboratorsByCompanyAndId] ERRO ao buscar colaborador:", error?.message || error);
        throw new Error(error?.message || "Erro ao buscar colaborador.");
    }
}


// ----------------------------------------------------------------


export const createCollaborator = async (companyId: Id, payload: CreateCollaboratorPayload): Promise<void> => {
    await request.post(`/empresas/${companyId}/colaboradores`, payload);
}

export const updateCollaborator = async (companyId: Id, collaboratorId: Id, payload: CreateCollaboratorPayload): Promise<void> => {
    await request.put(`/empresas/${companyId}/colaboradores/${collaboratorId}`, payload);
}

export const getCollaboratorServices = async (
    companyId: Id,
    collaboratorId: Id,
    startDate: string,
    endDate: string,
): Promise<Service[]> => {
    const response = await request.get<Service[]>(`/empresas/${companyId}/colaboradores/${collaboratorId}/services`, {
        params: {
            start_date: startDate,
            end_date: endDate,
        },
    });

    if (!response?.data) {
        return [];
    }

    return response.data.map(normalizeServiceStatus);
}

export const getCollaboratorServicesSummary = async (
    companyId: Id,
    collaboratorId: Id,
    startDate: string,
    endDate: string,
): Promise<CollaboratorServicesSummary> => {
    const response = await request.get<CollaboratorServicesSummary>(`/empresas/${companyId}/colaboradores/${collaboratorId}/services-summary`, {
        params: {
            start_date: startDate,
            end_date: endDate,
        },
    });

    return response.data;
}

export const getCompanyServices = async (companyId: Id, startDate: string, endDate: string): Promise<Service[]> => {
    const response = await request.get<Service[]>(`/empresas/${companyId}/services`, {
        params: {
            start_date: startDate,
            end_date: endDate,
        },
    });

    if (!response.data) {
        return []
    }

    return response.data.map(normalizeServiceStatus);
}

export const getCompanyServicesForDownload = async (companyId: Id, startDate: string, endDate: string): Promise<any> => {
    const response = await request.get<Service[]>(`/empresas/${companyId}/services/download`, {
        params: {
            start_date: startDate,
            end_date: endDate,
        },
        responseType: "blob",
    });

    if (!response.data) {
        return []
    }

    return response.data;
}

export type CompanyServiceSummary = {
    qtde_atendimentos: number;
    qtde_acoes: number;
    montante_total_atendimentos: number;
    montante_mensalidade: number;
    montante_total_atendimentos_e_mensalidade: number;
    nome_empresa: string;
    colaboradores_atendidos: string[];
    psicologos: string[];
}

export const getCompanyServiceSummary = async (companyId: Id, startDate: string, endDate: string): Promise<CompanyServiceSummary> => {
    const response = await request.get<CompanyServiceSummary>(`/empresas/${companyId}/services-summary`, {
        params: {
            start_date: startDate,
            end_date: endDate,
        },
    });

    return response.data;
}

export const deleteCollaboratorById = async (companyId: Id, collaboratorId: Id): Promise<void> => {
    await request.delete(`/empresas/${companyId}/colaboradores/${collaboratorId}`)
}


export const softDeleteCollaborator = async (currentCompanyId: Id, collaboratorId: Id, targetCompanyId: Id) => {
  await supabaseHttp.post("/rpc/soft_delete_collaborator", {
    current_company_id: Number(currentCompanyId),
    collaborator_id: Number(collaboratorId),
    target_company_id: Number(targetCompanyId),
  });
};

export const setCompanyHiddenStatus = async (companyId: Id, hidden: boolean): Promise<void> => {
    await supabaseHttp.patch(`/empresas?id=eq.${companyId}`, {
        oculto: hidden,
    }, {
        headers: {
            Prefer: 'return=representation',
        },
    });
}

export const getCompanyEezyoneEnabled = async (companyId: Id): Promise<boolean> => {
    const { data } = await supabaseHttp.get<{ eezyone_enabled: boolean }[]>(
        `/empresas?id=eq.${companyId}&select=eezyone_enabled`
    );
    const row = (data ?? [])[0];
    return row?.eezyone_enabled ?? true;
}

export const setCompanyEezyoneEnabled = async (companyId: Id, enabled: boolean): Promise<void> => {
    await supabaseHttp.patch(`/empresas?id=eq.${companyId}`, {
        eezyone_enabled: enabled,
    }, {
        headers: {
            Prefer: 'return=representation',
        },
    });
}

export const massiveCollaboratorFileUpload = async (companyId: Id, payload: FormData): Promise<void> => {
    await request.post(`/empresas/${companyId}/colaboradores/cadastro-massivo`, payload, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    })
}

export const toggleCollaboratorBlockById = async (companyId: Id, collaboratorId: Id, reason: string|null): Promise<void> => {
    await request.post(`/empresas/${companyId}/colaboradores/${collaboratorId}/toggle-block`, {
        reason: reason,
    })
}

export type CollaboratorOpenSession = {
    id_atendimento: number;
    data: string | null;
    valor: number;
}

export type CollaboratorOpenSessionsSummary = {
    id_empresa: number;
    id_colaborador: number;
    colaborador: string;
    bloqueio: boolean;
    qtde_sessoes_em_aberto: number;
    valor_total_em_aberto: number;
    sessoes_em_aberto: CollaboratorOpenSession[];
}

export type CollaboratorPaymentResult = {
    id_empresa: number;
    id_colaborador: number;
    colaborador: string;
    qtde_sessoes_quitadas: number;
    atendimentos_quitados: number[];
    valor_total_pago: number;
    valor_informado: number;
    desbloqueado: boolean;
    bloqueio: boolean;
}

export const getCollaboratorOpenSessions = async (
    companyId: Id,
    collaboratorId: Id,
): Promise<CollaboratorOpenSessionsSummary> => {
    const res = await request.get<CollaboratorOpenSessionsSummary>(
        `/empresas/${companyId}/colaboradores/${collaboratorId}/sessoes-em-aberto`,
    );

    return res.data;
}

/**
 * Registra o pagamento das sessões em aberto do colaborador. Quando o valor
 * cobre o total devido, a API quita todos os atendimentos e desbloqueia
 * automaticamente o colaborador.
 */
export const payCollaboratorOpenSessions = async (
    companyId: Id,
    collaboratorId: Id,
    amount?: number,
    reason?: string,
): Promise<CollaboratorPaymentResult> => {
    const res = await request.post<CollaboratorPaymentResult>(
        `/empresas/${companyId}/colaboradores/${collaboratorId}/pagamento`,
        {
            ...(amount !== undefined ? { valor: amount } : {}),
            ...(reason ? { reason } : {}),
        },
    );

    return res.data;
}

export const toggleCollaboratorCompanyBlockById = async (
    companyId: Id,
    collaboratorId: Id,
    isBlocked: boolean,
): Promise<void> => {
    await request.post(`/empresas/${companyId}/colaboradores/${collaboratorId}/toggle-company-block`, {
        is_blocked: isBlocked,
    })
}

export const sendCollaboratorSSOLink = async (
    companyId: Id,
    collaboratorId: Id,
    email: string,
): Promise<void> => {
    await request.post(`/sso/empresas/${companyId}/colaboradores/${collaboratorId}/send-link`, { email })
}
